import * as Sentry from '@sentry/react';
import { Adapter } from './types';
import { AnalyticsEvent } from '../events';
import { EVENT_META, isConversionEvent } from '../eventMeta';
import {
  getClientId,
  getDeviceId,
  getSessionId,
  getSessionElapsedMs,
} from '../identity';

// Low-cardinality dimensions — safe as metric attributes.
const dimensions = (event: AnalyticsEvent) => ({
  merchant: event.payload.merchant ?? 'unknown',
  template: event.payload.template,
  payment_method: event.payload.paymentMethod ?? 'unknown',
});

// Full attribute set incl. high-cardinality identity — for spans and logs.
const fullAttributes = (event: AnalyticsEvent) => ({
  ...dimensions(event),
  client_id: getClientId(),
  device_id: getDeviceId(),
  session_id: getSessionId(event.payload.uuid),
  // CPAYMENT-1717: time since the visit started, on every event. Time to first action, time spent
  // before leaving and the gap between steps all become subtractions at analysis time — no extra
  // events in the dictionary for what is already a timestamp difference.
  elapsed_ms: event.payload.uuid ? getSessionElapsedMs(event.payload.uuid) : 0,
  ...(event.payload as unknown as Record<string, unknown>),
});

export const sentryAdapter: Adapter = {
  name: 'sentry',
  send(event: AnalyticsEvent) {
    const meta = EVENT_META[event.name];
    const metricAttributes = dimensions(event);

    // 1. Span — the event chain / drill-down backbone (+ future Replay anchor).
    try {
      Sentry.startSpan(
        { name: event.name, op: 'ui.analytics', attributes: fullAttributes(event) },
        () => undefined,
      );
    } catch {
      // analytics must never break UX
    }

    // 2. Metrics — funnel KPIs, low-cardinality attributes only.
    try {
      Sentry.metrics.count(`analytics.${event.name}`, 1, { attributes: metricAttributes });
      if (isConversionEvent(event)) {
        Sentry.metrics.count('analytics.conversion', 1, { attributes: metricAttributes });
      }
      Sentry.metrics.distribution('analytics.goal_depth', meta.funnelStep, {
        attributes: metricAttributes,
      });
      if (event.payload.uuid) {
        Sentry.metrics.distribution(
          'analytics.session_elapsed_ms',
          getSessionElapsedMs(event.payload.uuid),
          { unit: 'millisecond', attributes: metricAttributes },
        );
      }
    } catch {
      // swallow
    }

    // 3. Logs — discrete diagnostic events with full context.
    try {
      if (meta.log) {
        Sentry.logger[meta.log](event.name, fullAttributes(event));
      }
    } catch {
      // swallow
    }

    // 4. Breadcrumb — kept for error context and future Replay.
    try {
      Sentry.addBreadcrumb({
        category: 'analytics',
        message: event.name,
        level: 'info',
        data: event.payload as unknown as Record<string, unknown>,
      });
    } catch {
      // swallow
    }
  },
};
